import { useGetUserQuery } from '@/api/piloteer';
import { coachQueryOptions } from '@/api/piloteer/coach';
import { EXTERNAL_LINKS } from '@/constants/external-links';
import { DashLayout } from '@/ui/@components/layouts/dash-layout/dash-layout';
import { useUserRole } from '@/ui/@hooks/use-user-role';
import { APP_ROUTES } from '@/ui/application-routes';
import { companyPerformancePartnersEnabled } from '@/ui/pages/coach/company-feature';
import { cn } from '@/utils/tailwind';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

export const SettingsLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const user = useGetUserQuery(undefined, coachQueryOptions);
  const ppEnabled =
    !user.error && companyPerformancePartnersEnabled(user.currentData?.company);
  const { t } = useTranslation();
  const { isCompanyAdmin } = useUserRole();

  const settingsItems: Array<{
    to: string;
    label: string;
    activeRoutes?: string[];
  }> = [
    {
      to: APP_ROUTES.SETTINGS.PROFILE,
      label: t('nav.profile'),
    },
    ...(ppEnabled
      ? [
          {
            to: APP_ROUTES.SETTINGS.COACHING,
            label: t('nav.performancePartner'),
          },
        ]
      : []),
    ...(isCompanyAdmin
      ? [
          {
            to: APP_ROUTES.COMPANY.USERS,
            label: t('nav.people'),
          },
          {
            to: APP_ROUTES.COMPANY.TEAMS,
            label: t('nav.teams'),
            activeRoutes: [APP_ROUTES.COMPANY.TEAM_DETAIL],
          },
          {
            to: APP_ROUTES.SETTINGS.KNOWLEDGE_BASE,
            label: t('companySettings.knowledgeBase'),
          },
          {
            to: APP_ROUTES.SETTINGS.LANGUAGE,
            label: t('companySettings.language'),
          },
          {
            to: APP_ROUTES.SETTINGS.COMPANY_CONTROLS,
            label: t('nav.companyControls'),
          },
        ]
      : []),
    {
      to: APP_ROUTES.SETTINGS.INTEGRATIONS,
      label: t('nav.integrations'),
    },
    {
      to: APP_ROUTES.SETTINGS.BILLING,
      label: t('nav.billing'),
    },
    {
      to: APP_ROUTES.SETTINGS.SECURITY,
      label: t('nav.security'),
    },
    {
      to: APP_ROUTES.SETTINGS.COMMANDER,
      label: t('nav.downloadConsole'),
    },
  ];

  const isActiveSettingsItem = (item: (typeof settingsItems)[number]) => {
    const routes = [item.to, ...(item.activeRoutes ?? [])];
    return routes.some((route) => {
      if (route.includes(':')) {
        const routeSegments = route.split('/').filter(Boolean);
        const currentSegments = location.pathname.split('/').filter(Boolean);

        return (
          routeSegments.length === currentSegments.length &&
          routeSegments.every(
            (segment, index) =>
              segment.startsWith(':') || segment === currentSegments[index]
          )
        );
      }

      return location.pathname === route;
    });
  };

  return (
    <DashLayout>
      <div className='mx-auto grid w-full max-w-6xl gap-2'>
        <h1 className='text-3xl font-semibold'>{t('nav.settings')}</h1>
      </div>
      <div className='mx-auto grid w-full max-w-6xl items-start gap-6 md:grid-cols-[180px_1fr] lg:grid-cols-[250px_1fr]'>
        <nav className='grid gap-4 text-sm text-muted-foreground'>
          {settingsItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                isActiveSettingsItem(item) && 'font-semibold text-primary'
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>
        <div className='flex flex-col gap-6'>
          {children}
          <p className='text-sm text-muted-foreground'>
            {t('settings.supportPrefix')}{' '}
            <a
              href={EXTERNAL_LINKS.SUPPORT_EMAIL}
              className='font-medium text-primary underline underline-offset-4'
            >
              support@piloteer.ai
            </a>
            .
          </p>
        </div>
      </div>
    </DashLayout>
  );
};
